import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import {
  selectCustomerSourceTypes,
  selectCustomerSourceTypesIsEmpty,
  selectCustomerSourceTypesLoading,
} from 'store/features/customer-source-types/selector';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import customerSourceTypesActions from 'store/features/customer-source-types/actions';
import CreateCustomerSourceTypeDialog from './components/create-customer-source-type-dialog';
import UpdateCustomerSourceTypeDialog from './components/update-customer-source-type-dialog';

const CustomerSourceTypes = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectCustomerSourceTypesLoading);
  const customerSourceTypes = useAppSelector(selectCustomerSourceTypes);
  const isEmptyCustomerSourceTypes = useAppSelector(selectCustomerSourceTypesIsEmpty);

  // useState hooks
  const [openDeleteCustomerSourceType, setOpenDeleteCustomerSourceType] = useState(false);
  const [openCreateCustomerSourceType, setOpenCreateCustomerSourceType] = useState(false);
  const [openUpdateCustomerSourceType, setOpenUpdateCustomerSourceType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'customerSourceTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(customerSourceTypesActions.getCustomerSourceTypes());
  };

  // sync helper functions
  const handleCloseDeleteCustomerSourceType = () => setOpenDeleteCustomerSourceType(false);
  const handleClickOpenDeleteCustomerSourceType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteCustomerSourceType(true);
  };

  const handleCloseCreateCustomerSourceType = () => setOpenCreateCustomerSourceType(false);
  const handleClickOpenCreateCustomerSourceType = () => setOpenCreateCustomerSourceType(true);

  const handleCloseUpdateCustomerSourceType = () => setOpenUpdateCustomerSourceType(false);
  const handleClickOpenUpdateCustomerSourceType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateCustomerSourceType(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!customerSourceTypes.length && !isEmptyCustomerSourceTypes) {
      dispatch(customerSourceTypesActions.getCustomerSourceTypes());
    }
  }, [customerSourceTypes, isEmptyCustomerSourceTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-customer-source-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateCustomerSourceType}>
              <FormattedMessage
                id="customerSourceTypes.createCustomerSourceType"
                defaultMessage="Müşteri Kaynağı Oluştur"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-customer-source-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateCustomerSourceType}>
              <FormattedMessage
                id="customerSourceTypes.updateCustomerSourceType"
                defaultMessage="Müşteri Kaynağı Düzenle"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-customer-source-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteCustomerSourceType}>
              <FormattedMessage
                id="customerSourceTypes.deleteCustomerSourceType"
                defaultMessage="Müşteri Kaynağı Sil"
              />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateCustomerSourceTypeDialog
        open={openCreateCustomerSourceType}
        handleClose={handleCloseCreateCustomerSourceType}
        refresh={refresh}
      />
      <UpdateCustomerSourceTypeDialog
        open={openUpdateCustomerSourceType}
        customerSourceTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateCustomerSourceType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteCustomerSourceType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteCustomerSourceType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={
          <FormattedMessage id="customerSourceTypes.deleteCustomerSourceType" defaultMessage="Müşteri Kaynağı Sil" />
        }
        subTitle={
          <FormattedMessage
            id="customerSourceTypes.deleteCustomerSourceTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra müşteri kaynağı veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteCustomerSourceType}
        successMessage={
          <FormattedMessage
            id="customerSourceTypes.deleteCustomerSourceTypeCompleted"
            defaultMessage="Seçili müşteri kaynağını silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={customerSourceTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default CustomerSourceTypes;
